import * as React from 'react';
import {useSelector} from 'react-redux';

import get from 'lodash/get';
import moment from 'moment';
import {Field, formValueSelector, reduxForm} from 'redux-form';
// @ts-expect-error
import type {FormProps} from 'redux-form/lib/types.js.flow';

import {FormField} from 'shells/form/form-field';
import {nonNull} from 'shells/form/validation/non-null';
import {ReduxFormDateTime} from 'shells/redux-form-fields/date-time';
import {ReduxFormTextField} from 'shells/redux-form-fields/textfield';

import type {Task as TaskFragment, RecurrenceRuleInput} from 'nutshell-graphql-types';

import type {NutshellState} from '../../../store';

import {EntitiesSelect} from '../../core/form/fields';
import {AssigneeSelectField} from '../../core/form/fields/assignee-select-field';
import type {FlashMessage} from '../../creator/types';

import {DefaultFooter} from '../default-footer/default-footer';
import {RecurrenceFormField} from '../fields/recurrence-form-field';
import {TaskFooter} from '../footers/task-footer';
import {FormWrapper} from '../form-wrapper/form-wrapper';

import styles from '../form-common.module.css';

type Props = {
    // This will only exist when viewing the task page tasks/{slug}
    task: TaskFragment | null | undefined;
    isReadOnly: boolean;
    onCancelClick: () => any;
    onUpdateTask: () => any;
    handleSubmit: FormProps['handleSubmit'];
    reset: FormProps['reset'];
    submitting: FormProps['submitting'];
    submitFailed: FormProps['submitFailed'];
    change: FormProps['change'];
    pushFlashMessage: (arg1: FlashMessage) => void;
    // `form` is used by redux-form
    form: string;
};

export function TaskFormConnectedComponent(props: Props) {
    const selector = formValueSelector(props.form);
    const dueTime = useSelector((state: NutshellState) => selector(state, 'dueTime'));
    const recurrenceRule: any = useSelector((state: NutshellState) =>
        selector(state, 'recurrenceRule')
    );

    function handleCancelClick() {
        props.onCancelClick();
        props.reset();
    }

    const handleRecurrenceRuleChange = (nextRule: RecurrenceRuleInput | null) => {
        props.change('recurrenceRule', nextRule);
    };

    const handleToggleTaskCompletion = (isCompleting: boolean) => {
        props.pushFlashMessage({
            message: `This task was ${isCompleting ? 'completed' : 'updated'} successfully.`,
        });

        // Callback to refetch our task when updated
        props.onUpdateTask();
    };

    const footerComponent =
        props.task && props.isReadOnly ? (
            <TaskFooter
                task={props.task}
                onToggleTaskCompletion={handleToggleTaskCompletion}
                onUpdateTask={props.onUpdateTask}
            />
        ) : (
            <DefaultFooter
                onCancel={handleCancelClick}
                isSaving={props.submitting}
                errorSaving={props.submitFailed}
                buttonText={props.task ? undefined : 'Create task'}
                savingText={props.task ? undefined : 'Creating task…'}
            />
        );

    return (
        <form onSubmit={props.handleSubmit} className={styles.form}>
            {/* @ts-expect-error */}
            <FormWrapper entityType='tasks' reset={props.reset} footer={footerComponent}>
                <Field
                    name='title'
                    title='Name'
                    disabled={props.isReadOnly}
                    component={ReduxFormTextField}
                    isRequired={true}
                    autoFocus={!NutshellFrontendSettings.isDemoSilo}
                />
                {shouldIncludeField('dueTime') ? (
                    <Field
                        name='dueTime'
                        title='Due date'
                        disabled={props.isReadOnly}
                        component={ReduxFormDateTime}
                    />
                ) : null}
                {NutClientConfig.hasRecurringTasksFF && shouldIncludeField('recurrenceRule') ? (
                    <RecurrenceFormField
                        recurrenceRule={recurrenceRule ? recurrenceRule : null}
                        onRecurrenceRuleChange={handleRecurrenceRuleChange}
                        referenceDatetime={dueTime ? moment(dueTime).unix() : null}
                        isDisabled={props.isReadOnly}
                    />
                ) : null}
                {shouldIncludeField('links.assignee') ? (
                    <AssigneeSelectField
                        fieldKey='links.assignee'
                        isRequired={true}
                        // @ts-expect-error
                        validate={[nonNull]}
                        isDisabled={props.isReadOnly}
                    />
                ) : null}
                {shouldIncludeField('links.relatedEntity') ? (
                    <FormField
                        key='links.relatedEntity'
                        innerId='links.relatedEntity'
                        label='Related to'
                    >
                        <Field
                            name='links.relatedEntity'
                            component={EntitiesSelect}
                            isMulti={false}
                            disabled={props.isReadOnly}
                            isLinkable={true}
                        />
                    </FormField>
                ) : null}
                {shouldIncludeField('description') ? (
                    <Field
                        name='description'
                        title='Notes'
                        disabled={props.isReadOnly}
                        multiline={true}
                        component={ReduxFormTextField}
                    />
                ) : null}
            </FormWrapper>
        </form>
    );

    function shouldIncludeField(fieldName: string) {
        // If we're in edit mode, always include the field
        if (!props.isReadOnly) return true;

        // If we somehow opened the form in readOnly without a task, hide everything.  :(
        if (!props.task) return false;

        if (fieldName === 'links.assignee') {
            return props.task.assignee;
        }

        if (fieldName === 'links.relatedEntity') {
            return props.task.relatedEntity;
        }

        if (fieldName === 'recurrenceRule') {
            // Only show the recurrence field in read-only mode if the task actually
            // has a rule; otherwise it would render an empty "Does not repeat" row.
            return Boolean(props.task.recurrenceRule);
        }

        // If read only mode, we only want to show non-empty fields
        return get(props.task, fieldName);
    }
}

// @ts-expect-error
export const TaskFormConnected = reduxForm()(TaskFormConnectedComponent);
